import React, { useEffect, useMemo, useRef, useState } from 'react';
import CarouselComponent from '../../../Misc/CarouselComponent/CarouselComponent';
import styles from './styles.module.scss';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import { BarLoader, PuffLoader, PulseLoader, RiseLoader } from 'react-spinners';

import errorMsg from '../../../Functions/errorMsg';
import successMsg from '../../../Functions/successMsg';

import { faAd, faBookmark, faCheck, faChevronDown, faChevronUp, faCreditCard, faEye, faFlag, faLocationDot, faMessage, faPaperPlane, faPhone, faShare, faStar, faTicket, faXmark, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import openAdManager from '../../../Functions/openAdManager';

import Image from 'next/image';
import { useInView } from 'framer-motion';
import { useRouter } from 'next/router';

import MapWithLocation from '../../../Misc/MapWithLocation/MapWithLocation';
import { faStripe } from '@fortawesome/free-brands-svg-icons';

import infoMsg from '../../../Functions/infoMsg';

import { motion } from 'framer-motion';
import { useSocket } from '../../../Context/socket';

import OnlineProfile from '../../UserProfile/OnlineProfile/OnlineProfile';

export default function EventListing({ listingID, closeListing }) {

    const router = useRouter();
    const { data: session, status } = useSession();
    const { socket, socketConnected } = useSocket();

    const [initialLoading, setInitialLoading] = useState(null);

    const [data, setData] = useState(null);
    const [posterProfile, setPosterProfile] = useState(null);
    const [reviewData, setReviewData] = useState(null);

    const [reported, setReported] = useState(false);

    useEffect(() => {
        if(initialLoading !== null) return;
        if(data !== null) return;
        if(status === 'loading') return;

        const requestingUser = status === 'unauthenticated' ? 'nouser' : session.user.token.username;

        async function getListingData() {
            setInitialLoading(true);

            const result = await axios.post(`${window.location.origin}/api/eventHub/getEvent`, {
                listingID: listingID,
                editData: false,
                requestingUser: requestingUser
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString())
                closeListing(null);
                setInitialLoading(false);
            })

            if(result && result.status === 200) {
                const listingData = result.data;
                setData(listingData.listing);
                setPosterProfile(listingData.profile);
                setReviewData(returnRating(listingData.listing.reviews));
                setReported(listingData.listing.reports.some((report) => report.reportingUser === requestingUser))
            }

            setInitialLoading(false);
        }

        getListingData();

    }, [session, status])

    function isLoading() {
        if(status === 'loading') return true;
        if(initialLoading === true || initialLoading === null) return true;
        if(data === null) return true;
        if(posterProfile === null) return true;
        if(reviewData === null) return true;
        return false;
    }

    function authenticationIsOwnerCheck() {
      if(!session) return false;
      if(status !== 'authenticated') return false;
      if(session.user.token.username === data.owner) return false;
      return true;
    }
    
    const { displayTime, displayTimeType } = createdTimeStamp(data?.creationDate);

    const memoizeCountdown = useMemo(() => {
      if (data) {
        return <TimeStampContainer start={data.startDate} end={data.endDate} />;
      } else {
        return null; // or any default value
      }
    }, [data]);
    
    const memoizeCarousel = useMemo(() => {
      if (data && data.images.length > 0) {
        return <CarouselComponent listingImages={data.images} />;
      } else {
        return null; // or any default value
      }
    }, [data]);

    function isAcceptingPayments() {
      if(status === 'loading') return false;
      return data.acceptPayments.enabled;
    }

    function userAuthenticated() {
      if(!session) return false;
      if(status !== 'authenticated') return false;
      if(!session.user.token.id) return false;
      return true;
    }

    const [desiredPurchases, setDesiredPurchases] = useState([]);

    const [creatingSession, setCreatingSession] = useState(false);

    async function handlePurchase() {
      if(creatingSession === true) return;
      if(data === null) return errorMsg("Could not create checkout session, please contact us: contact@wacoexchange.com")
      if(!session || status !== 'authenticated') return infoMsg("You must be signed in before purchasing tickets")

      setCreatingSession(true);

      const result = await axios.post(`${window.location.origin}/api/eventHub/tickets/createCheckout`, {
        eventID: data._id,
        userID: session.user.token.id,
        purchases: desiredPurchases
      }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString())
        console.error(error.toString());
      })

      if(result && result.status === 200) {
        const url = result.data;
        window.open(url, '_blank');
      }

      setCreatingSession(false);

    }

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [viewUserProfile, openUserProfile] = useState(false);

    const [createReport, setCreateReport] = useState(null);

    const [loadingHasConvo, setLoadingHasConvo] = useState(false);
    const [hasConvo, setHasConvo] = useState(null);

    useEffect(() => {
        if(loadingHasConvo || hasConvo !== null) return;
        if(!session) return;
        if(status === 'loading') return;
        if(!data) return;
        if(!posterProfile) return;

        const controller = new AbortController();

        const profileUsername = posterProfile.username;

        async function getUserProfile() {
            setLoadingHasConvo(true);
            const result = await axios.post(`${window.location.origin}/api/messaging/hasActiveConvo`, {
                signal: controller.signal,
                requestingUserID: session.user.token.id,
                otherUser: profileUsername,
                convoType: 'Events',
                listingID: data._id
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString());
            })

            if(result && result.status === 200) {
                const data = result.data;
                setHasConvo({ hasConvo: data.hasConvo, convoID: data.convoID });
            }

            setLoadingHasConvo(false);
        }

        getUserProfile();

        return () => {
            controller.abort();
        }

    }, [status, posterProfile, data, posterProfile])

    const quickMessageTextRef = useRef(null);

    async function handleQuickMessageSend() {
        if(loadingHasConvo) return;

        if(!posterProfile) return errorMsg("There was an error sending your message, please try again later.");

        if(!session) return errorMsg("Please login to message this user.");
        if(status === 'loading') return;
        if(status === 'unauthenticated') return errorMsg("Please login to message this user.");

        if(!socket) return errorMsg("There was an error sending your message, please try again later.");
        if(!socketConnected) return errorMsg("There was an error sending your message, please try again later.");

        if(!quickMessageTextRef || !quickMessageTextRef.current) return;

        const messageText = quickMessageTextRef.current.value;

        if(messageText.length === 0) return errorMsg("Please enter a message before submitting.");
        if(messageText.length > 1000) return errorMsg("Your message is too long, please submit below 1000 characters.");

        const toUser = posterProfile.username;

        const listingID = data._id;

        const senderID = session.user.token.id;
        const sender = session.user.token.username;

        const basicConvoData = {
            type: 'Events',
            listingID: listingID,
            receiver: toUser,
            sender: sender,
            creationDate: new Date().toISOString()
        }

        setLoadingHasConvo(true);

        const result = await axios.post(`${window.location.origin}/api/messaging/create`, {
            conversationData: basicConvoData,
            messageText: messageText,
            senderID: senderID,
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        });

        if(result && result.status === 200) {
            const newConvoData = result.data;

            const hasNewConvoData = {
                hasConvo: true,
                convoID: newConvoData._id
            }

            setHasConvo(hasNewConvoData)

            socket.emit('createNewConversation', newConvoData)

            successMsg("Your message was sent.");

        }

        setLoadingHasConvo(false);

    }

    function handleOpenCurrentConversation() {
        if(!hasConvo) return errorMsg("Could not find this conversation, please try again later");
        if(hasConvo.hasConvo === false) return errorMsg("You do not have a conversation with this user.");

        const { query } = router;

        const updatedQuery = { ...query, openConversationID: hasConvo.convoID };

        router.push({ pathname: router.pathname, query: updatedQuery });
    }

    const memoizeOptionBar = useMemo(() => <OptionBar listing={data} session={session} closeListing={closeListing} authenticationIsOwnerCheck={authenticationIsOwnerCheck} handleCreateReport={() => setCreateReport({listingID: data._id, reportingUser: data.owner})} reported={reported} />, [data, session, closeListing, reported])

  return (
    <div id={styles.wrapper}>
        { !reported && createReport && <CreateReportBox listingID={createReport.listingID} reportingUser={createReport.reportingUser} closeReport={() => setCreateReport(null)} successfulReport={() => setReported(true)} /> }
        { viewUserProfile && <OnlineProfile profileUsername={posterProfile.username} closeProfile={() => openUserProfile(false)} /> }
        { isLoading() === true && <PulseLoader size={30} color='white' /> }
        { isLoading() === false && 
        <div id={styles.listing}>

            {memoizeOptionBar}

            <div id={styles.infoContainer}>

                <section id={styles.map}>
                  <MapWithLocation address={data.eventAddress} city={data.eventCity} zipCode={data.zip} />
                  <div id={styles.listingInfoDots}>
                    <div style={{display: data.views.length === 0 ? 'none' : 'flex'}}>
                      <FontAwesomeIcon icon={faEye} id={styles.icon} />
                      <h1>{data.views.length}</h1>
                    </div>
                    <div style={{display: data.watching.length === 0 ? 'none' : 'flex'}}>
                      <FontAwesomeIcon icon={faBookmark} id={styles.icon} />
                      <h1>{data.watching.length}</h1>
                    </div>
                  </div>
                </section>

                <div style={{marginTop: '25px', marginBottom: '10px'}} id={styles.rentalPropertyLocation} onClick={() => { navigator.clipboard.writeText(`${data.eventAddress}, ${data.eventCity}, TX ${data.zip}`); successMsg("Property address copied to clipboard") }}>
                    <FontAwesomeIcon icon={faLocationDot} id={styles.icon} />
                    <div>
                        <h1>{data.eventAddress}, {data.eventCity}, TX {data.zip}</h1>
                        <h2>Click to copy the event address</h2>
                    </div>
                </div>

                <div style={{marginBottom: '25px'}} id={styles.rentalPropertyLocation} onClick={() => window.open(`tel:${data.contactPhone}`)}>
                    <FontAwesomeIcon icon={faPhone} id={styles.icon} />
                    <div>
                        <h1>{data.contactPhone}</h1>
                        <h2>Click to call</h2>
                    </div>
                </div>

                <section id={styles.info}>
                    <h1>{data.title}</h1>
                    <h2>{uppercaseFirst(data.eventCity)}, TX</h2>
                    <h3>Listing posted {displayTime} {displayTimeType}</h3>
                    <hr />
                    <p style={{ display: data.description.length === 0 ? 'none' : 'flex'}}>{data.description}</p>
                    {
                      data.eventFeatures && <div id={styles.rentalPropertyFeatures} style={{display: data.eventFeatures.length === 0 ? 'none' : 'flex', marginTop: '25px'}}>
                        <h1>Features & Amenities</h1>
                        <section>
                            {
                                data.eventFeatures.map((feature, index) => {
                                    return <h1 key={index}>{feature}</h1>
                                })
                            }
                        </section>
                      </div>
                    }
                    <hr />
                    { memoizeCountdown }
                    <hr />

                    {isAcceptingPayments() === true && (data && getTimeStamp(data.startDate, data.endDate)).status === 'Upcoming...' && <ShopContainer userAuthenticated={userAuthenticated} creatingSession={creatingSession} eventData={data} desiredPurchases={desiredPurchases} setDesiredPurchases={setDesiredPurchases} handlePurchase={handlePurchase} />}
    
                    <hr style={{display: isAcceptingPayments() === true && (data && getTimeStamp(data.startDate, data.endDate)).status === 'Upcoming...' ? 'flex' : 'none'}} />
                </section>
                <section id={styles.carouselContainer}>
                  {memoizeCarousel}
                </section>
                <hr />

                <section id={styles.posterProfile}>
                    <div>
                        <span style={{display: profileLoaded === false ? 'flex' : 'none'}}> <PuffLoader color='#154734' size={50} /> </span>
                        <Image sizes='auto auto' fill src={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} alt={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} onLoadingComplete={() => setProfileLoaded(true)} />
                    </div>
                    <div>
                        <h1>{posterProfile.displayName}</h1>
                        { reviewData > 0 && <section>
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 1 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 2 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 3 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 4 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon icon={faStar} color={reviewData === 5 ? '#FFB81C' : 'lightgrey'} />
                        </section> }
                    </div>
                    <div>
                        <button onClick={() => openUserProfile(true)}>
                            <FontAwesomeIcon icon={faEye} id={styles.view} />
                        </button>
                    </div>
                </section>

                <section style={{display: authenticationIsOwnerCheck() ? 'flex' : 'none' }} id={styles.quickMessage}>
                    { !hasConvo || loadingHasConvo ?
                    <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}><BarLoader width={200} height={5} color='white' /></span>
                    :
                    <>
                        <div>
                            <FontAwesomeIcon icon={faMessage} id={styles.icon} />
                            <h1>{ !hasConvo.hasConvo ? 'Quick Message' : 'View Conversation' }</h1>
                        </div>
                        <div>
                            { !hasConvo.hasConvo && <textarea ref={quickMessageTextRef} placeholder="Write this user a message..." /> }
                            <FontAwesomeIcon onClick={!hasConvo.hasConvo ? handleQuickMessageSend : handleOpenCurrentConversation} icon={!hasConvo.hasConvo ? faPaperPlane : faEye} id={styles.icon} />
                        </div>
                    </>
                    }
                </section>

                <AdvertisementSpot adID={getAdSpotID(data.listArea)} session={session} router={router} closeListing={closeListing} />
            </div>

        </div>
        }

    </div>
  )
}

const ShopContainer = ({ userAuthenticated, creatingSession, eventData, desiredPurchases, setDesiredPurchases, handlePurchase }) => {

  const router = useRouter();

  const purchaseOptions = eventData.acceptPayments.purchaseOptions;
  const refundPolicy = eventData.acceptPayments.refundPolicy;

  const ShopCard = ({ item }) => {

    const desiredPurchase = desiredPurchases.find(purchase => purchase.name === item.name);

    const handleIncrease = (itemName) => {
      const desiredPurchaseIndex = desiredPurchases.findIndex(purchase => purchase.name === itemName);
      if (desiredPurchaseIndex === -1) {
        // If the item is not in desiredPurchases and availableQuantity is greater than 0, add it with a quantity of 1
        if (item.availableQuantity > 0) {
          setDesiredPurchases([
            ...desiredPurchases,
            { id: item.id, name: itemName, price: item.price, desc: item.desc, merchantUserID: item.merchantUserID , quantity: 1 }
          ]);
        }
      } else {
        // If the item is in desiredPurchases, increment its quantity if it's less than availableQuantity
        const updatedPurchases = [...desiredPurchases];
        if (updatedPurchases[desiredPurchaseIndex].quantity < item.availableQuantity) {
          updatedPurchases[desiredPurchaseIndex].quantity++;
          setDesiredPurchases(updatedPurchases);
        }
      }
    };
    
    const handleDecrease = (itemName) => {
      const desiredPurchaseIndex = desiredPurchases.findIndex(purchase => purchase.name === itemName);
      if (desiredPurchaseIndex !== -1) {
        // If the item is in desiredPurchases, decrement its quantity if it's greater than 0
        const updatedPurchases = [...desiredPurchases];
        if (updatedPurchases[desiredPurchaseIndex].quantity > 0) {
          updatedPurchases[desiredPurchaseIndex].quantity--;
          setDesiredPurchases(updatedPurchases.filter(purchase => purchase.quantity > 0));
        }
      }
    };

    return (
      <div className={styles.card} style={{ opacity: !userAuthenticated() ? '0.7' : '1.0', pointerEvents: !userAuthenticated() ? 'none' : 'auto' }}>
        <section>
          <h1>${item.price}</h1>
          <h2>{item.availableQuantity === 0 ? 'Sold Out' : `${item.availableQuantity} Left`}</h2>
        </section>

        <section>
          <h1>{item.name}</h1>
          <p>{item.desc}</p>
        </section>

        <section style={{display: item.availableQuantity === 0 ? 'none' : 'flex'}}>
          <div>
            <button onClick={() => handleIncrease(item.name)}><FontAwesomeIcon icon={faChevronUp} /></button>
            <button onClick={() => handleDecrease(item.name)}><FontAwesomeIcon icon={faChevronDown} /></button>
          </div>
          <h1>{desiredPurchase ? desiredPurchase.quantity : 0}<FontAwesomeIcon icon={faTicket} id={styles.icon} /></h1>
        </section>
      </div>
    )
  }

  const calculateOverallTotal = () => {
    let total = 0;
    desiredPurchases.forEach(purchase => {
      total += parseInt(purchase.quantity) * parseFloat(purchase.price);
    });
    return total;
  };

  const TicketSalesEndH = () => {
    const [endStamps, setEndStamps] = useState(null);

    useEffect(() => {
        if(!eventData) return;
        if(endStamps !== null) return;
    
        const countdownInterval = setInterval(() => {
  
          var stamps = getTimeStamp(eventData.startDate, eventData.endDate);
          var str;

          if(stamps.status === 'Upcoming...') {
            str = `${stamps.days}D : ${stamps.hours}H : ${stamps.minutes}M : ${stamps.seconds}S`
          } else {
            str = "Not accepting payments"
          }
  
          if(stamps.status === 'Ended' || stamps?.status === undefined) {
            closeCountdown();
          }
  
          setEndStamps({ stamps: stamps, str: str });
  
        }, 100);
    
        function closeCountdown() {
          clearInterval(countdownInterval);
        }
    
        return () => {
          clearInterval(countdownInterval)
        }
  
    }, [eventData])
    return (
      <h1><b style={{fontWeight: '500'}}>Ticket sales end:</b> {endStamps && endStamps.str}</h1>
    )
  }

  const memoizeTicketSalesEndH = useMemo(() => <TicketSalesEndH />, []);

  return ( 
    <section id={styles.acceptingPayments} >
      {memoizeTicketSalesEndH}
      <section>


      { !userAuthenticated() ?
        <button onClick={() => router.push('/account/login')} style={{pointerEvents: userAuthenticated() ? 'none' : 'auto' }} >Login To Purchase</button>
        :
        <button disabled={desiredPurchases.length === 0 ? true : false} onClick={handlePurchase} style={{pointerEvents: !userAuthenticated() ? 'none' : 'auto' }}  >
        {
          creatingSession === true ? (
            <BarLoader color='white' height={10} />
          ) : (
            calculateOverallTotal() !== 0 ? (
              <>
                {`$${calculateOverallTotal().toLocaleString()} -`}
                <FontAwesomeIcon
                  style={{marginLeft: calculateOverallTotal() !== 0 ? '5px' : '0px'}}
                  icon={faCreditCard}
                  id={styles.icon}
                />
                Purchase Now
              </>
            )
            :
            'No Selection'
          )
        }
        </button>
        }

      <span id={styles.stripeDesign}>
        <h1>Powered by</h1>
        <FontAwesomeIcon icon={faStripe} id={styles.icon} />
      </span>
      </section>
      <div id={styles.shop}>
        { purchaseOptions.map((item, index) => <ShopCard key={index} item={item} />) }
      </div>
      <div id={styles.refundPolicy}>
        <h1>Refund Policy</h1>
        <p>{refundPolicy}</p>
      </div>
    </section>
  )
}

const TimeStampContainer = ({ start, end }) => {

  const [timeStamps, setTimeStamps] = useState(null);

  useEffect(() => {
    if(start === null || end === null) return;

    const countdownInterval = setInterval(() => {

      const timestamp = getTimeStamp(start, end);
      if(timestamp.status === 'Ended' || timestamp?.status === undefined) {
        closeCountdown();
      }
      setTimeStamps(timestamp);
    }, 100);

    function closeCountdown() {
      clearInterval(countdownInterval);
    }

    return () => {
      clearInterval(countdownInterval)
    }

  }, [start, end])

    return timeStamps === null ?
      <section id={styles.countdown}>
        <PulseLoader size={20} color='#292929' />
      </section>
      :
      <>
        { timeStamps.status === 'Upcoming...' && <h1>Upcoming Event</h1> }
        { timeStamps.status === 'Active' && <h1>In Session</h1> }
        { timeStamps.status === 'Ended' && <h1>Event Ended</h1> }
        <section style={{display: timeStamps.status === "Ended" ? 'none' : 'flex'}} id={styles.countdown}>
        <div>
          <h1>Days</h1>
          <h2>{timeStamps.days}</h2>
        </div>
        <div>
          <h1>Hrs</h1>
          <h2>{timeStamps.hours}</h2>
        </div>
        <div>
          <h1>Mins</h1>
          <h2>{timeStamps.minutes}</h2>
        </div>
        <div>
          <h1>Secs</h1>
          <h2>{timeStamps.seconds}</h2>
        </div>
      </section>
   
      </> }

const CreateReportBox = ({ listingID, reportingUser, closeReport, successfulReport }) => {

  const { data: session, status } = useSession();

  const [pendingReport, setPendingReport] = useState(false);

  const reportTextRef = useRef(null);
  const [reportReason, setReportReason] = useState(null);

  const generateReport = async () => {
      if(listingID === null) return;
      if(pendingReport === true) return;
      if(!session) return;
      if(status !== 'authenticated') return;
      if(reportReason === null) return errorMsg("Please select a report reason.")
      if(reportTextRef === null || reportTextRef.current.value.length === 0) return errorMsg("Please enter some details about your report.");
      if(reportTextRef.current.value.length > 500) return errorMsg("Please enter some details about your report.");

      setPendingReport(true);

      const result = await axios.post(`${window.location.origin}/api/TFSSystem/createReport`, {
        requestingUserID: session.user.token.id,
        origin: 'Events',
        reportedUser: reportingUser,
        reasonType: reportReason,
        message: reasonText,
        listingID: listingID,
        creationDate: new Date().toISOString()
      }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString());
      })
  
      if(result !== undefined) {
          if(result.status === 200) {
              closeReport();
              successfulReport();
              successMsg("Listing has been reported, thank you.");
          } else {
              errorMsg("We ran into an issue, please try again later.");
          }
      }
      setPendingReport(false);
  }

  return (
      <motion.section initial={{x: listingID === null ? '-320px' : '0'}} animate={{x: listingID === null ? '-320px' : '0', transition: { bounce: '0' }}} id={styles.reportBox}>
        <FontAwesomeIcon onClick={closeReport} icon={faXmark} id={styles.exit} />
        <h1>Create Report</h1>

        <div id={styles.types}>
            <button onClick={() => setReportReason("Inappropriate Content")} disabled={pendingReport} style={reportReason === "Inappropriate Content" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Inappropriate Content</button>
            <button onClick={() => setReportReason("Spam or Scams")} disabled={pendingReport} style={reportReason === "Spam or Scams" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Spam or Scams</button>
            <button onClick={() => setReportReason("Safety Concerns")} disabled={pendingReport} style={reportReason === "Safety Concerns" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Safety Concerns</button>
            <button onClick={() => setReportReason("Illegal Activity")} disabled={pendingReport} style={reportReason === "Illegal Activity" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Illegal Activity</button>
            <button onClick={() => setReportReason("Privacy Violation")} disabled={pendingReport} style={reportReason === "Privacy Violation" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Privacy Violation</button>
            <button onClick={() => setReportReason("Offensive Behavior")} disabled={pendingReport} style={reportReason === "Offensive Behavior" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Offensive Behavior</button>
        </div>

        <textarea maxLength={500} disabled={pendingReport} ref={reportTextRef} style={{display: reportReason !== null ? 'flex' : 'none'}} placeholder='Please give us some detail...' />
        <button disabled={pendingReport} onClick={generateReport} style={{display: reportReason !== null ? 'flex' : 'none'}}>
            {pendingReport ? <BarLoader color='white' /> : 'Submit Report'}
        </button>
      </motion.section>
  )
}
  
  function getTimeStamp(startTime, endTime) {
  
    const starting = new Date(Date.parse(startTime));
    const ending = new Date(Date.parse(endTime));
    const current = new Date();
  
    var years;
    var months;
    var weeks;
    var days;
    var hours;
    var minutes;
    var seconds;
    var status;
  
    if(current < starting) {
      const dif = Math.abs((starting.getTime() - current.getTime()) / 1000)
      years = Math.floor(dif / 31536000);
      months = Math.floor((dif % 31536000) / 2628000);
      weeks = Math.floor(dif / 31536000);
      days = Math.floor(dif / (3600 * 24));
      hours = Math.floor(dif % (3600*24) / 3600);
      minutes = Math.floor(dif % 3600 / 60);
      seconds = Math.floor(dif % 60);
      status = "Upcoming..."
    } else if(current >= starting && current <= ending) {
      const dif = Math.abs((ending.getTime() - current.getTime()) / 1000)
      years = Math.floor(dif / 31536000);
      months = Math.floor((dif % 31536000) / 2628000);
      weeks = Math.floor(dif / 31536000);
      days = Math.floor(dif / (3600 * 24));
      hours = Math.floor(dif % (3600*24) / 3600);
      minutes = Math.floor(dif % 3600 / 60);
      seconds = Math.floor(dif % 60);
      status = "Active"
    } else if(current >= ending) {
      years = 0;
      months = 0;
      weeks = 0;
      days = 0;
      hours = 0;
      minutes = 0;
      seconds = 0;
      status = "Ended";
    }
  
    return { years, months, weeks, days, hours, minutes, seconds, status }
  }

  const OptionBar = ({ listing, session, closeListing, authenticationIsOwnerCheck, handleCreateReport, reported }) => {

    const [watching, setWatching] = useState(listing?.watching?.some(watchers => session?.user?.token?.username === watchers));
    const [watchPending, setWatchPending] = useState(false);

    async function apiHandleWatch() {
        if(watchPending === true) return;
        setWatchPending(true);

        const username = session.user.token.username;
        const listingID = listing._id;

        if(watching === false) {
          const result = await axios.post(`${window.location.origin}/api/eventHub/watch-methods/watch`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
              if(error?.response?.data?.status !== undefined) {
                  errorMsg(error?.response?.data?.status);
                  setWatchPending(false)
              } else {
                  errorMsg(error);
                  setWatchPending(false)
              }
          })
      
          if(result !== undefined) {
              if(result.status === 200) {
                  setWatching(true);
                  setWatchPending(false)
                  successMsg("Listing saved.");
              } else {
                  errorMsg('There was a problem verifying your account, please try again later.');
                  setWatchPending(false)
              }
          }
        }
  
        if(watching === true) {
          const result = await axios.post(`${window.location.origin}/api/eventHub/watch-methods/unwatch`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
              if(error?.response?.data?.status !== undefined) {
                  errorMsg(error?.response?.data?.status);
                  setWatchPending(false)
              } else {
                  errorMsg(error);
                  setWatchPending(false)
              }
          })
      
          if(result !== undefined) {
              if(result.status === 200) {
                  setWatching(false);
                  setWatchPending(false)
                  successMsg("Listing unsaved.")
              } else {
                  errorMsg('There was a problem verifying your account, please try again later.');
                  setWatchPending(false)
              }
          }
        }
    }

    return (
        <span id={styles.optionBar}>
            <span>
                { authenticationIsOwnerCheck() && <button id={styles.reportButton} onClick={() => reported === false && handleCreateReport()}> <FontAwesomeIcon icon={faFlag} id={styles.icon} color={reported === true ? '#FFB81C' : 'white'} /> </button> }
                { authenticationIsOwnerCheck() && <button id={styles.saveButton} onClick={() => apiHandleWatch()}>

                { watchPending === false && <FontAwesomeIcon icon={faBookmark} id={styles.icon} color={watching === true ? '#FFB81C' : 'white'} /> }
                { watchPending === true && <PuffLoader size={28} color='white' /> }

                </button> }
                <button id={styles.shareButton} onClick={() => {navigator.clipboard.writeText(`${window.location.origin}/marketplace/events/?popup=sharedPost&_id=${listing._id}`); successMsg("Link Copied") } } > <FontAwesomeIcon icon={faShare} id={styles.icon} /> </button>
                <button id={styles.exitButton} onClick={() => closeListing(null)} > <FontAwesomeIcon icon={faXmarkCircle} id={styles.icon} /> </button>
            </span>
        </span>
    )
}

const AdvertisementSpot = ({ adID, session, router, closeListing }) => {

    const adContainerRef = useRef(null);
  
    const adInView = useInView(adContainerRef, { once: true })
    const [adShown, setAdShown] = useState(false);
    const [adData, setAdData] = useState({ img: null, url: null });
  
    useEffect(() => {
      if(adInView === false) return;
      if(adShown === true) return;
      
      async function getAdImage() {
        setAdShown(true); // Ad was in view port
        setAdData({ img: true, url: null }); // Loading State
  
        const retrieveAdImg = await axios.post(`${window.location.origin}/api/admanager/retrieveAd`, {
          adID: adID
        }).catch((error) => {
          console.error(error?.response?.data?.status || error.toString())
        })
  
        if(retrieveAdImg !== undefined) {
          if(retrieveAdImg.status === 200) {
            const img = retrieveAdImg.data.image; //img link
            const url = retrieveAdImg.data.url; //url link
            
            const structure = {
              img: img === 'NO_IMAGE' ? false : img,
              url: url
            }
  
            setAdData(structure);
          } else {
            console.error("There was an error when loading the advertisement image.");
          }
        }
      }
  
      getAdImage();
  
    }, [adInView])
  
    async function handleClick(e) {
      e.preventDefault();
      window.open(adData.url, '_blank');
  
      await axios.post(`${window.location.origin}/api/admanager/countClick`, {
        adID: adID
      }).catch((error) => {
        console.error(error.toString());
      })
    }
  
    const [coverLoaded, setCoverLoaded] = useState(false);
  
    return (
      <section ref={adContainerRef} id={styles.adSpot} style={{cursor: 'pointer', borderWidth: adData.img !== null && adData.img !== true & adData.img !== false && adData.img !== "NO_IMAGE" ? 0 : null}}>
        { adData.img === null && <h1>Ad Spot Available</h1> }
        { adData.img === false && <h1>Ad Spot Available</h1> }
        { adData.img === true && <RiseLoader size={15} color='#154734' /> }
        { (adData.img === null || adData.img === false) && session !== null && session !== undefined && <button onClick={() => { openAdManager(router); closeListing(null)} }><FontAwesomeIcon icon={faAd} id={styles.icon} /> Manager</button>}
  
        { adData.img !== null && adData.img !== true && adData.img !== false && <>
          <h6>Sponsored Advertisement</h6>
          <span style={{width: '100%', height: '100%', display: coverLoaded === true ? 'none' : 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <RiseLoader size={15} color='#154734' />
          </span>
          <Image onClick={(e) => handleClick(e)} sizes='auto auto' style={{objectFit: 'contain'}} fill src={adData.img} alt={adData.img} onLoadingComplete={() => setCoverLoaded(true)} />
        </> }
      </section>
    )
  }

  function getAdSpotID(listingType) {
    var id = null;
    if(listingType === 'Marketplace') {
        id = 'prod_P697csIKcqIYoP';
    }

    if(listingType === 'Trading') {
        id = 'prod_P6975xlB4NnBce';
    }

    if(listingType === 'Rentals') {
        id = 'prod_P697Qt9mbaHsxh';
    }

    if(listingType === 'Estate') {
        id = 'prod_P697WjKSGBf7qw';
    }

    if(listingType === 'YGSale') {
        id = 'prod_P697Ls7mQcCMs8';
    }
    return id;
}

/* Additional Functions */
/* ==================== */



function uppercaseFirst(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function returnRating(reviews) {
    let rating;
    let totalStars = 0;
    if(reviews?.length === 0) {
        rating = 0;
    } else {
        reviews?.forEach((review) => {
            totalStars+= review?.rating;
        })
        rating = totalStars / reviews?.length;
    }
    rating = Math.ceil(parseFloat(rating.toFixed(2)));

    return rating;
}

function createdTimeStamp(creationDate) {
    let displayTime;
    let displayTimeType;

    var delta = Math.floor(new Date().getTime() - new Date(creationDate).getTime());

    var seconds = Math.floor(delta / 1000);
    var minutes = Math.floor(seconds / 60);
    var hours =  Math.floor(minutes / 60);
    var days = Math.floor(hours / 24);
    var weeks = Math.floor(days / 7);
    var months = Math.floor(weeks / 4);

    if(seconds <= 10) {
        displayTime = "just now";
        displayTimeType = ""
    } else {
        displayTime = seconds;
        displayTimeType = "seconds ago"
    }

    if(minutes > 1) {
        displayTime = minutes;
        displayTimeType = 'minutes ago'
    }

    if(minutes === 1) {
        displayTime = minutes;
        displayTimeType = 'minute ago'
    }

    if(hours > 1) {
        displayTime = hours;
        displayTimeType = 'hours ago'
    }

    if(hours === 1) {
        displayTime = hours;
        displayTimeType = 'hour ago'
    }
    
    if(days > 1) {
        displayTime = days;
        displayTimeType = 'days ago'
    }

    if(days === 1) {
        displayTime = days;
        displayTimeType = 'day ago'
    }

    if(weeks > 1) {
        displayTime = weeks;
        displayTimeType = 'weeks ago'
    }

    if(weeks === 1) {
        displayTime = weeks;
        displayTimeType = 'week ago'
    }

    if(months > 1) {
        displayTime = months;
        displayTimeType = 'months ago'
    }

    if(months === 1) {
        displayTime = months;
        displayTimeType = 'month ago'
    }

    return {displayTime, displayTimeType}
}